import { match as localeMatcher } from '@formatjs/intl-localematcher';
import { extractAppConfig } from '../app-config';

export enum SupportedLanguageType {
    EN = 'en',
    ES = 'es',
    FR = 'fr',
    DE = 'de',
    IT = 'it',
    JA = 'ja',
    KO = 'ko',
    PL = 'pl',
    PT = 'pt',
    ZH = 'zh',
}

export enum SupportedLocale {
    ENus = 'en-US',
    ENgb = 'en-GB',
    ENca = 'en-CA',
    EN = 'en',
    ES = 'es',
    FR = 'fr',
    DE = 'de',
    IT = 'it',
    JA = 'ja',
    KO = 'ko',
    PL = 'pl',
    PT = 'pt',
    PTbr = 'pt-BR',
    ZH = 'zh',
    ZHtw = 'zh-TW',
}

export const defaultLanguage = SupportedLanguageType.EN;
export const defaultLocale = SupportedLocale.EN;

// Helper function to parse Accept-Language header with quality value handling
function parseAcceptLanguageHeader(header: string): string[] {
    return header
        .split(',')
        .map((lang) => {
            const parts = lang.trim().split(';');
            const locale = parts[0].trim();
            const qValue = parts[1]
                ? parseFloat(parts[1].replace('q=', ''))
                : 1;
            return { locale, qValue };
        })
        .sort((a, b) => b.qValue - a.qValue) // Sort by quality value descending
        .map(({ locale }) => locale); // Extract just the locales in priority order
}

export function getLanguage(
    acceptLanguageHeader?: string | null
): SupportedLanguageType {
    const { NEXT_PUBLIC_SUPPORTED_LOCALES } = extractAppConfig();
    if (!acceptLanguageHeader) {
        return defaultLanguage;
    }

    // Parse Accept-Language header properly, handling quality values
    const requestedLocales = parseAcceptLanguageHeader(acceptLanguageHeader);

    // Filter out invalid locale codes (basic validation + known problematic codes)
    const validRequestedLocales = requestedLocales.filter((locale) => {
        const isValidFormat =
            /^[a-zA-Z][a-zA-Z-]*$/.test(locale) && locale.length >= 2;
        const isNotProblematic = !['an', 'AN'].includes(locale); // Filter known test environment issues
        return isValidFormat && isNotProblematic;
    });

    if (validRequestedLocales.length === 0) {
        return defaultLanguage;
    }

    // Use localeMatcher with just the base supported languages
    const matchedLanguage = localeMatcher(
        validRequestedLocales,
        NEXT_PUBLIC_SUPPORTED_LOCALES,
        defaultLanguage,
        { algorithm: 'lookup' }
    );

    // Ensure the matched language is valid and supported
    if (
        matchedLanguage &&
        NEXT_PUBLIC_SUPPORTED_LOCALES.includes(matchedLanguage)
    ) {
        return matchedLanguage as SupportedLanguageType;
    }

    return defaultLanguage;
}

export function getLocale(
    acceptLanguageHeader?: string | null
): SupportedLocale {
    if (!acceptLanguageHeader) {
        return defaultLocale;
    }

    const requestedLocales = parseAcceptLanguageHeader(acceptLanguageHeader);

    // Filter out invalid locale codes (basic validation + known problematic codes)
    const validRequestedLocales = requestedLocales.filter((locale) => {
        const isValidFormat =
            /^[a-zA-Z][a-zA-Z-]*$/.test(locale) && locale.length >= 2;
        const isNotProblematic = !['an', 'AN'].includes(locale); // Filter known test environment issues
        return isValidFormat && isNotProblematic;
    });

    if (validRequestedLocales.length === 0) {
        return defaultLocale;
    }

    // Use `localeMatcher` to find the best match with enhanced validation
    const supportedLocales = Object.values(SupportedLocale);
    const matchedLocale = localeMatcher(
        validRequestedLocales,
        supportedLocales,
        defaultLocale,
        { algorithm: 'lookup' }
    );

    // Handle special case: if we got 'zh' but we support 'zh-TW', prefer the specific variant
    if (
        matchedLocale === 'zh' &&
        supportedLocales.includes('zh-TW' as SupportedLocale)
    ) {
        return 'zh-TW' as SupportedLocale;
    }

    // Strict validation: only return locales that are actually in our supported enum
    if (supportedLocales.includes(matchedLocale as SupportedLocale)) {
        return matchedLocale as SupportedLocale;
    }

    // If localeMatcher returned something invalid (prevents fuzzy matching issues), fall back to default
    return defaultLocale;
}

export function determineRedirect(
    pathname: string,
    requestLocale: string,
    acceptLanguageHeader: string | null
) {
    const { NEXT_PUBLIC_SUPPORTED_LOCALES } = extractAppConfig();
    const language = getLanguage(acceptLanguageHeader);

    if (language === defaultLanguage) {
        // remove any locale from supported languages
        const regexp = new RegExp(
            `^/(${NEXT_PUBLIC_SUPPORTED_LOCALES.join('|')})`
        );
        const pathContainsLocale = regexp.test(pathname);

        if (!pathContainsLocale) {
            return;
        }

        const newUrl = pathname.replace(regexp, '');
        return newUrl;
    }

    if ((language as string) === requestLocale) {
        return;
    }

    return `/${language}${pathname}`;
}
